import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  LinkIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/20/solid';
import { Loader2 } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import {
  DataTable,
  humanizeStatus,
  type MetricBoxConfig,
  MetricsBoxes,
  StatusPill,
  type TableColumn,
} from '@/components/common/DataTable';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { usePagination } from '@/hooks/usePagination';
import { useGetIntakesQuery } from '@/services/api/intakeService';
import {
  type Lead,
  useCreateIntakeDraftMutation,
  useGetLeadsQuery,
  useLinkIntakeMutation,
  useSyncCaseXChangeMutation,
  useUpdateLeadStatusMutation,
} from '@/services/api/leadIntegrationService';

import { LeadDetailDrawer } from './LeadDetailDrawer';

// Stable reference to avoid RTK Query infinite refetch
const STABLE_QUERY_PARAMS = {};

const PROVIDER_LABELS: Record<string, string> = {
  lexamica: 'Lexamica',
  casexchange: 'CaseXChange',
};

const STATUS_OPTIONS = [
  { label: 'New', value: 'new' },
  { label: 'Evaluating', value: 'reviewing' },
  { label: 'Intake Created', value: 'intake_created' },
  { label: 'Converted', value: 'converted' },
  { label: 'Rejected', value: 'rejected' },
  { label: 'Closed', value: 'closed' },
];

const PROVIDER_OPTIONS = [
  { label: 'Lexamica', value: 'lexamica' },
  { label: 'CaseXChange', value: 'casexchange' },
];

function formatRelativeDate(iso?: string | null): string {
  if (!iso) return '-';
  const date = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ---------------------------------------------------------------------------
// Reject dialog
// ---------------------------------------------------------------------------

const RejectLeadDialog = ({
  open,
  onOpenChange,
  onConfirm,
  isSubmitting,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason: string) => Promise<void>;
  isSubmitting: boolean;
}) => {
  const [reason, setReason] = useState('');

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) setReason('');
        onOpenChange(v);
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Reject Lead</DialogTitle>
          <DialogDescription>
            This will notify the referring provider that the lead was declined.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="lead-reject-reason">Reason (optional)</Label>
          <Textarea
            id="lead-reject-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Conflict, outside jurisdiction, duplicate..."
            rows={3}
          />
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => onConfirm(reason).catch(() => undefined)}
            disabled={isSubmitting}
          >
            {isSubmitting && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Reject
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Link Intake dialog
// ---------------------------------------------------------------------------

const LinkIntakeDialog = ({
  open,
  onOpenChange,
  onConfirm,
  isSubmitting,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (intakeId: string) => Promise<void>;
  isSubmitting: boolean;
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedIntakeId, setSelectedIntakeId] = useState<string | null>(null);

  React.useEffect(() => {
    if (!open) {
      setSearchTerm('');
      setDebouncedSearch('');
      setSelectedIntakeId(null);
    }
  }, [open]);

  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  React.useEffect(() => {
    setSelectedIntakeId(null);
  }, [debouncedSearch]);

  const { data: intakesData, isFetching } = useGetIntakesQuery(
    { search: debouncedSearch, limit: 20, offset: 0 },
    { skip: !open || debouncedSearch.length < 2 },
  );
  const intakes = intakesData?.results ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Link to Intake</DialogTitle>
          <DialogDescription>
            Search for an existing intake to link to this lead.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by client name, phone, or email..."
              className="pl-9"
            />
          </div>
          <div className="max-h-64 min-h-[120px] overflow-y-auto rounded-md border">
            {debouncedSearch.length < 2 ? (
              <div className="flex h-[120px] items-center justify-center text-sm text-gray-500">
                Type at least 2 characters to search
              </div>
            ) : isFetching ? (
              <div className="flex h-[120px] items-center justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
              </div>
            ) : intakes.length === 0 ? (
              <div className="flex h-[120px] items-center justify-center text-sm text-gray-500">
                No intakes found
              </div>
            ) : (
              <div className="divide-y">
                {intakes.map((intake) => (
                  <button
                    key={intake.id}
                    type="button"
                    className={`w-full px-3 py-2.5 text-left transition-colors hover:bg-gray-50 ${
                      selectedIntakeId === intake.id
                        ? 'bg-blue-50 ring-1 ring-inset ring-blue-200'
                        : ''
                    }`}
                    onClick={() =>
                      setSelectedIntakeId((prev) =>
                        prev === intake.id ? null : intake.id,
                      )
                    }
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900">
                        {intake.client_name || 'Unknown'}
                      </span>
                      <span className="text-xs text-gray-500">
                        {intake.status}
                      </span>
                    </div>
                    <div className="mt-0.5 text-xs text-gray-500">
                      {intake.client_phone || '-'}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (selectedIntakeId) {
                onConfirm(selectedIntakeId).catch(() => undefined);
              }
            }}
            disabled={isSubmitting || !selectedIntakeId}
          >
            {isSubmitting && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Link Intake
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------

const createLeadColumns = (
  onCreateIntake: (lead: Lead) => void,
  onReject: (lead: Lead) => void,
  onViewIntake: (intakeId: string) => void,
  onLinkIntake: (lead: Lead) => void,
  isCreatingDraft: boolean,
): TableColumn<Lead>[] => [
  {
    id: 'client_name',
    header: 'Client',
    accessorFn: (row) =>
      row.normalized_payload?.client_full_name || 'Unknown',
    width: '280px',
    sticky: true,
    cell: (row) => {
      const name = row.normalized_payload?.client_full_name || 'Unknown';
      const contact =
        row.normalized_payload?.client_phone ||
        row.normalized_payload?.client_email;
      const canCreate =
        !row.intake_id &&
        (row.status === 'new' || row.status === 'reviewing');
      const canReject =
        row.status === 'new' || row.status === 'reviewing';

      return (
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <span
              className="text-black dark:text-white truncate block"
              title={name}
            >
              {name}
            </span>
            {contact && (
              <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5 truncate">
                {contact}
              </div>
            )}
          </div>
          <div
            className="flex items-center gap-0.5 shrink-0"
            data-stop-row-click="true"
          >
            {canCreate && (
              <button
                type="button"
                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-950/40 transition-colors"
                onClick={() => onCreateIntake(row)}
                disabled={isCreatingDraft}
              >
                <PlusIcon className="h-3.5 w-3.5" />
                Intake
              </button>
            )}
            {row.intake_id && (
              <>
                <button
                  type="button"
                  className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-zinc-600 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:bg-zinc-800 transition-colors"
                  onClick={() => row.intake_id && onViewIntake(row.intake_id)}
                >
                  <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                  Intake
                </button>
                <button
                  type="button"
                  className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-zinc-500 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:bg-zinc-800 transition-colors"
                  onClick={() => onLinkIntake(row)}
                >
                  <LinkIcon className="h-3 w-3" />
                  Change
                </button>
              </>
            )}
            {canReject && (
              <button
                type="button"
                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40 transition-colors"
                onClick={() => onReject(row)}
              >
                <XMarkIcon className="h-3.5 w-3.5" />
                Reject
              </button>
            )}
            {!row.intake_id && !canCreate && (
              <button
                type="button"
                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-zinc-600 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:bg-zinc-800 transition-colors"
                onClick={() => onLinkIntake(row)}
              >
                <LinkIcon className="h-3.5 w-3.5" />
                Link Intake
              </button>
            )}
          </div>
        </div>
      );
    },
    truncate: false,
  },
  {
    id: 'provider',
    header: 'Source',
    accessorFn: (row) => PROVIDER_LABELS[row.provider] ?? row.provider,
    width: '120px',
    filter: {
      type: 'multiselect',
      options: PROVIDER_OPTIONS,
    },
  },
  {
    id: 'referring_firm',
    header: 'Referring Firm',
    accessorFn: (row) => row.referring_firm_name || '-',
    width: '180px',
  },
  {
    id: 'case_type',
    header: 'Case Type',
    accessorFn: (row) => row.normalized_payload?.case_type || '-',
    width: '160px',
    cell: (row) => {
      const caseType = row.normalized_payload?.case_type;
      if (!caseType) return <span className="text-zinc-400">-</span>;
      return <span>{humanizeStatus(caseType)}</span>;
    },
    filter: {
      type: 'multiselect',
      options: [
        { label: 'Motor Vehicle Accident', value: 'automobile_accident' },
        { label: 'Premises Liability', value: 'slip_and_fall' },
        { label: 'Medical Malpractice', value: 'medical_malpractice' },
        { label: 'Wrongful Death', value: 'wrongful_death' },
        { label: 'Dog Bite', value: 'animal_incident' },
        { label: 'Miscellaneous', value: 'other' },
      ],
    },
  },
  {
    id: 'incident_state',
    header: 'State',
    accessorFn: (row) => row.normalized_payload?.incident_state || '-',
    width: '70px',
  },
  {
    id: 'status',
    header: 'Lead Status',
    accessorKey: 'status',
    width: '140px',
    cell: (row) => <StatusPill status={row.status} size="sm" />,
    filter: {
      type: 'multiselect',
      options: STATUS_OPTIONS,
    },
  },
  {
    id: 'intake_status',
    header: 'Intake',
    accessorFn: (row) => row.intake_status || '',
    width: '130px',
    cell: (row) => {
      if (!row.intake_status)
        return <span className="text-zinc-400">&mdash;</span>;
      return <StatusPill status={row.intake_status} size="sm" />;
    },
  },
  {
    id: 'created_at',
    header: 'Received',
    accessorFn: (row) => {
      const cxcDate = (row.raw_payload as Record<string, unknown>)?.createdAt;
      return (cxcDate as string) || row.created_at;
    },
    width: '100px',
    cell: (row) => {
      const cxcDate = (row.raw_payload as Record<string, unknown>)?.createdAt as string | undefined;
      const date = cxcDate || row.created_at;
      return (
        <span
          className="text-zinc-500 dark:text-zinc-400"
          title={new Date(date).toLocaleString()}
        >
          {formatRelativeDate(date)}
        </span>
      );
    },
  },
];

// ---------------------------------------------------------------------------
// Metric boxes config
// ---------------------------------------------------------------------------

const METRIC_FILTER_KEYS = ['status'];

function buildMetricBoxes(leads: Lead[]): MetricBoxConfig[] {
  const counts = { new: 0, reviewing: 0, intake_created: 0, converted: 0, rejected: 0 };
  leads.forEach((lead) => {
    if (lead.status in counts) {
      counts[lead.status as keyof typeof counts] += 1;
    }
  });
  return [
    {
      id: 'new',
      label: 'New',
      count: counts.new,
      tooltip: 'Leads awaiting review',
      filters: { status: ['new'] },
    },
    {
      id: 'reviewing',
      label: 'Evaluating',
      count: counts.reviewing,
      tooltip: 'Leads under evaluation',
      filters: { status: ['reviewing'] },
    },
    {
      id: 'intake_created',
      label: 'Intake Created',
      count: counts.intake_created,
      tooltip: 'Leads converted to intake drafts',
      filters: { status: ['intake_created'] },
    },
    {
      id: 'converted',
      label: 'Signed',
      count: counts.converted,
      tooltip: 'Leads signed and converted to matters',
      filters: { status: ['converted'] },
    },
    {
      id: 'rejected',
      label: 'Rejected',
      count: counts.rejected,
      tooltip: 'Leads declined',
      filters: { status: ['rejected'] },
    },
  ];
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export const LeadsPage = () => {
  const navigate = useNavigate();
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [rejectTarget, setRejectTarget] = useState<Lead | null>(null);
  const [linkIntakeTarget, setLinkIntakeTarget] = useState<Lead | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [columnFilters, setColumnFilters] = useState<
    Record<string, string | string[] | null>
  >({});

  // usePagination for metric box filter integration
  const { state: paginationState, actions: paginationActions } = usePagination({
    defaultLimit: 1000,
  });

  const { data: leads = [], isLoading, refetch } = useGetLeadsQuery(STABLE_QUERY_PARAMS);

  const [updateStatus, { isLoading: isUpdating }] =
    useUpdateLeadStatusMutation();
  const [createDraft, { isLoading: isCreatingDraft }] =
    useCreateIntakeDraftMutation();
  const [syncCxc, { isLoading: isSyncing }] = useSyncCaseXChangeMutation();
  const [linkIntake, { isLoading: isLinking }] = useLinkIntakeMutation();

  const handleLinkIntake = async (intakeId: string) => {
    if (!linkIntakeTarget) return;
    try {
      await linkIntake({ leadId: linkIntakeTarget.id, intakeId }).unwrap();
      toast.success('Intake linked to lead');
      setLinkIntakeTarget(null);
    } catch {
      toast.error('Failed to link intake');
    }
  };

  const handleSync = async () => {
    try {
      const result = await syncCxc().unwrap();
      toast.success(
        result.processed > 0
          ? `Synced ${result.processed} new lead${result.processed === 1 ? '' : 's'}`
          : 'Already up to date',
      );
      refetch();
    } catch {
      toast.error('Failed to sync leads');
    }
  };

  const metricBoxes = useMemo(() => buildMetricBoxes(leads), [leads]);

  // Client-side filtering + default sort by received date (newest first)
  const filteredLeads = useMemo(() => {
    let result = [...leads].sort((a, b) => {
      const dateA = (a.raw_payload as Record<string, unknown>)?.createdAt as string || a.created_at;
      const dateB = (b.raw_payload as Record<string, unknown>)?.createdAt as string || b.created_at;
      return new Date(dateB).getTime() - new Date(dateA).getTime();
    });

    // Metric box filter (from pagination state)
    const metricStatus = paginationState.filters.status;
    if (metricStatus) {
      const statuses = Array.isArray(metricStatus) ? metricStatus : [metricStatus];
      result = result.filter((l) => statuses.includes(l.status));
    }

    // Column filters
    const providerFilters = columnFilters.provider;
    if (Array.isArray(providerFilters) && providerFilters.length) {
      result = result.filter((l) => providerFilters.includes(l.provider));
    }
    const statusFilters = columnFilters.status;
    if (Array.isArray(statusFilters) && statusFilters.length) {
      result = result.filter((l) => statusFilters.includes(l.status));
    }
    const caseTypeFilters = columnFilters.case_type;
    if (Array.isArray(caseTypeFilters) && caseTypeFilters.length) {
      result = result.filter((l) =>
        caseTypeFilters.includes(l.normalized_payload?.case_type || '')
      );
    }

    // Search
    if (searchInput.trim()) {
      const q = searchInput.toLowerCase().trim();
      result = result.filter((l) => {
        const name = (l.normalized_payload?.client_full_name || '').toLowerCase();
        const phone = (l.normalized_payload?.client_phone || '').toLowerCase();
        const email = (l.normalized_payload?.client_email || '').toLowerCase();
        const firm = (l.referring_firm_name || '').toLowerCase();
        return name.includes(q) || phone.includes(q) || email.includes(q) || firm.includes(q);
      });
    }

    return result;
  }, [leads, paginationState.filters, columnFilters, searchInput]);

  const handleCreateIntake = async (lead: Lead) => {
    try {
      const result = await createDraft(lead.id).unwrap();
      const intakeId = result.lead.intake_id;
      if (intakeId) {
        toast.success('Intake draft created');
        navigate(`/intakes/${intakeId}`);
      }
    } catch {
      toast.error('Failed to create intake draft');
    }
  };

  const handleReject = async (reason: string) => {
    if (!rejectTarget) return;
    try {
      await updateStatus({
        id: rejectTarget.id,
        status: 'rejected',
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      }).unwrap();
      toast.success('Lead rejected');
      setRejectTarget(null);
    } catch {
      toast.error('Failed to reject lead');
    }
  };

  const columns = useMemo(
    () =>
      createLeadColumns(
        (lead) => {
          handleCreateIntake(lead).catch(() => undefined);
        },
        setRejectTarget,
        (intakeId) => navigate(`/intakes/${intakeId}`),
        setLinkIntakeTarget,
        isCreatingDraft,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isCreatingDraft, navigate],
  );

  return (
    <div className="flex h-[calc(100vh-64px)] flex-col gap-2 overflow-hidden bg-gray-50 px-10 py-2 dark:bg-zinc-950">
      {/* Header — matching Matters page exactly */}
      <div className="flex shrink-0 items-center justify-between">
        <h1 className="font-serif text-4xl font-medium text-black dark:text-white">
          Leads
        </h1>
        <MetricsBoxes
          boxes={metricBoxes}
          paginationState={paginationState}
          paginationActions={paginationActions}
          metricFilterKeys={METRIC_FILTER_KEYS}
          loading={isLoading}
        />
      </div>

      {/* Table — matching Matters container exactly */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-gray-300 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
        <DataTable
          data={filteredLeads}
          columns={columns}
          getRowKey={(row) => row.id}
          searchEnabled
          disableClientFilter
          searchPlaceholder="Search by client name, phone, or firm..."
          searchValue={searchInput}
          onSearchChange={setSearchInput}
          searchResultsText={
            filteredLeads.length !== leads.length
              ? `${filteredLeads.length} of ${leads.length} leads`
              : `${leads.length} leads`
          }
          actions={[
            {
              label: 'Clear all filters',
              icon: <XMarkIcon className="h-4 w-4 shrink-0" />,
              onClick: () => {
                setSearchInput('');
                setColumnFilters({});
                paginationActions.reset();
              },
              variant: 'plain' as const,
              collapseAt: 'lg' as const,
            },
            {
              label: isSyncing ? 'Syncing...' : 'Sync Leads',
              icon: <ArrowPathIcon className={`h-4 w-4 shrink-0 ${isSyncing ? 'animate-spin' : ''}`} />,
              onClick: () => {
                handleSync().catch(() => undefined);
              },
              color: 'blue' as const,
              className: 'bg-primary hover:bg-primary-dark !items-center',
            },
          ]}
          onFilterChange={(columnId, value) =>
            setColumnFilters((prev) => ({ ...prev, [columnId]: value }))
          }
          filterValues={columnFilters}
          isLoading={isLoading}
          totalCount={filteredLeads.length}
          onRowClick={setSelectedLead}
          emptyMessage="No leads found"
          emptyStateConfig={{
            title: 'No leads yet',
            description:
              'Leads will appear here once referral providers start sending cases.',
          }}
        />
      </div>

      {/* Detail drawer */}
      <LeadDetailDrawer
        leadId={selectedLead?.id ?? null}
        open={!!selectedLead}
        onClose={() => setSelectedLead(null)}
      />

      {/* Reject dialog */}
      <RejectLeadDialog
        open={!!rejectTarget}
        onOpenChange={(v) => {
          if (!v) setRejectTarget(null);
        }}
        onConfirm={handleReject}
        isSubmitting={isUpdating}
      />

      <LinkIntakeDialog
        open={!!linkIntakeTarget}
        onOpenChange={(v) => {
          if (!v) setLinkIntakeTarget(null);
        }}
        onConfirm={handleLinkIntake}
        isSubmitting={isLinking}
      />
    </div>
  );
};
